import { createContext, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getStoredSelectedCluster } from '../functions/clusterSwitch';

const POPUP_WIDTH = 260;

const SharedClusterPreviewContext = createContext(false);

/**
 * True when the page is rendered as a browsable preview on the shared cluster.
 * Pages read this to keep their forms usable while turning off the calls that
 * would create something the shared tier cannot run.
 */
export const useSharedClusterPreview = () => useContext(SharedClusterPreviewContext);

/**
 * Wraps a page that is only fully functional on dedicated clusters.
 * When the active cluster is "default" (shared tier):
 *   - Shows an amber notice at the top explaining the restriction.
 *   - Shows a small dismissible popup anchored to the cluster picker in the
 *     navbar, telling users they can switch clusters there.
 *   - Renders the page content beneath it, either frozen (default) or as a
 *     browsable preview when `browsable` is set. A preview stays clickable so
 *     visitors can walk the real screens; the page itself is responsible for
 *     disabling its mutations via `useSharedClusterPreview`.
 *
 * `browsableNotice` overrides the banner copy for pages where part of the
 * feature does work on the shared tier.
 */
export default function SharedClusterGate({ children, featureName, browsable = false, browsableNotice }) {
  const isDefault = getStoredSelectedCluster() === 'default';
  const [hintOpen, setHintOpen] = useState(true);
  // { top, left, arrowLeft } once the cluster picker has been located.
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!isDefault || !hintOpen) return undefined;

    const place = () => {
      const picker = document.querySelector('.nav-cluster-picker');
      if (!picker) {
        setPos(null);
        return;
      }
      const rect = picker.getBoundingClientRect();
      const pickerCenter = rect.left + rect.width / 2;
      // Keep the popup fully on-screen, then point the arrow at the picker.
      let left = pickerCenter - POPUP_WIDTH / 2;
      const maxLeft = window.innerWidth - POPUP_WIDTH - 12;
      left = Math.max(12, Math.min(left, maxLeft));
      setPos({
        top: rect.bottom + 10,
        left,
        arrowLeft: pickerCenter - left,
      });
    };

    // Dismiss the hint as soon as the user interacts with the cluster picker.
    const closeOnPickerClick = (e) => {
      if (e.target.closest && e.target.closest('.nav-cluster-picker')) {
        setHintOpen(false);
      }
    };

    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('click', closeOnPickerClick, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('click', closeOnPickerClick, true);
    };
  }, [isDefault, hintOpen]);

  if (!isDefault) return children;

  return (
    <div>
      <div className="scg-banner">
        <div className="scg-banner-left">
          <div className="scg-banner-icon">{browsable ? '🔎' : '🔒'}</div>
          <div>
            <div className="scg-banner-body">
              {browsable
                ? (browsableNotice
                  || 'Have a look around. The forms work, but creating anything here needs your own cluster (Gold or Premium).')
                : 'This feature is only available on dedicated clusters (Gold or Premium tier).'}{' '}
              <Link to="/cluster" className="scg-banner-link">
                Upgrade →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {hintOpen && pos && (
        <div
          className="scg-popup"
          role="dialog"
          aria-label="Switch cluster hint"
          style={{ top: pos.top, left: pos.left, width: POPUP_WIDTH }}
        >
          <span
            className="scg-popup-arrow"
            style={{ left: pos.arrowLeft }}
            aria-hidden="true"
          />
          <button
            type="button"
            className="scg-popup-close"
            aria-label="Dismiss"
            onClick={() => setHintOpen(false)}
          >
            ×
          </button>
          <div className="scg-popup-body">
            Already have a dedicated cluster? If you wish to switch cluster,{' '}
            <strong>click the cluster picker above.</strong>
          </div>
        </div>
      )}

      {browsable ? (
        <SharedClusterPreviewContext.Provider value={true}>
          {children}
        </SharedClusterPreviewContext.Provider>
      ) : (
        <div className="scg-locked-content" aria-hidden="true">
          {children}
        </div>
      )}
    </div>
  );
}
